import { FC, useMemo, useState, useEffect } from 'react';
import { Grid, Row, Col } from 'rsuite';
import SearchIcon from '@rsuite/icons/Search';

import { getComplement, getIntersection, getUnion } from 'utils/SetUtils';

import { RsInput, RsInputSize } from 'components/Rsuite/Input';
import { RsButton } from 'components/Rsuite/Button';

import {
  ManualKeywordProperty,
  ManualKeywordType,
  MANUAL_UNIQUE_KEY,
  SuggestKeywordProperty,
  SuggestKeywordType,
  SUGGEST_KEYWORD_UNIQUE_KEY,
} from 'core/rtbCampaign/singlePage/keyword';

import { useBidRange } from '../../../Hooks/useBidRange';

import { searchMatch } from './utils';
import { SuggestKeywordTable } from './SuggestKeywordTable';
import { ReadyKeywordTable } from './ReadyKeywordTable';
import styles from './manualKeywordEditBoard.module.scss';

const getSuggestKeywordsByKeys = (
  suggestKeywords: SuggestKeywordType[],
  keys: Set<SuggestKeywordType[typeof SUGGEST_KEYWORD_UNIQUE_KEY]>,
) => {
  return suggestKeywords
    .filter((item) => keys.has(item[SUGGEST_KEYWORD_UNIQUE_KEY]))
    .map((item) => ({
      [ManualKeywordProperty.ID]: undefined,
      [ManualKeywordProperty.KEYWORD]:
        item[SuggestKeywordProperty.SUGGEST_KEYWORD],
      [ManualKeywordProperty.PRICE]: Number(item[SuggestKeywordProperty.CPC]),
    }));
};

const OutlierKeyword = ({ show, value, onClick, disabled }) => {
  if (!show) return null;
  // Split keywords for display purposes if multiple are entered
  const displayValues = value
    .replace(/(\\n|<br\s*\/?>)/gi, '') // Remove newlines and <br> tags
    .split(/[,，]/) // Split by English (,) or Chinese (，) comma
    .map((v) => v.trim()) // Trim whitespace from each part
    .filter((v) => v.length > 0); // Filter out empty strings
  const displayString = displayValues.join(','); // Join with comma for display
  return (
    <div className='mt-2 flex items-center gap-[18px]'>
      <h2 className='text-[18px]'>加入指定關鍵字:</h2>
      <div className='relative max-h-[100px] flex-1 overflow-y-auto'>
        <span className='max-h-[100px] flex-1 overflow-y-auto text-sm font-semibold text-[--rs-primary-500]'>
          {displayString}
        </span>
      </div>
      {disabled ? (
        <RsButton appearance='primary' disabled>
          已加入
        </RsButton>
      ) : (
        <div>
          <RsButton
            appearance='ghost'
            onClick={onClick}
            disabled={displayValues.length > 1000}
            errorMessage={
              displayValues.length > 1000
                ? '關鍵字數量超過1000個，請重新調整'
                : undefined
            }
          >
            新增
          </RsButton>
        </div>
      )}
    </div>
  );
};

interface ManualKeywordEditBoardProps {
  chosen: ManualKeywordType[];
  onChosenChange: (v: ManualKeywordType[]) => void;
  suggestKeywords: SuggestKeywordType[];
  onCancel: () => void;
}

export const ManualKeywordEditBoard: FC<ManualKeywordEditBoardProps> = ({
  chosen,
  onChosenChange,
  suggestKeywords,
  onCancel,
}) => {
  const bidRange = useBidRange();
  const [readyKeywords, setReadyKeywords] = useState<ManualKeywordType[]>([]);
  const [searchValue, setSearchValue] = useState('');
  const trimmedSearchValue = searchValue.trim();

  const [hasInitializedChecked, setHasInitializedChecked] = useState(false);
  const trimmedSearchValueAlreadyIncluded = useMemo(
    () =>
      [...chosen, ...readyKeywords].some(
        (item) => item[ManualKeywordProperty.KEYWORD] === trimmedSearchValue,
      ),
    [chosen, trimmedSearchValue, readyKeywords],
  );

  const customKeywordShow = useMemo(() => {
    if (!trimmedSearchValue) return false;

    const searchValueFullyMatchKeyword = suggestKeywords.some(
      (item) =>
        item[SuggestKeywordProperty.SUGGEST_KEYWORD] === trimmedSearchValue,
    );

    if (searchValueFullyMatchKeyword) return false;

    const customKeywordBlacklist = ['|'];

    const invalidWordIncluded = customKeywordBlacklist.some((invalidValue) =>
      trimmedSearchValue.includes(invalidValue),
    );

    if (invalidWordIncluded) return false;

    return true;
  }, [trimmedSearchValue, suggestKeywords]);

  const filteredSuggestKeywords = useMemo(
    () =>
      suggestKeywords.filter((item) =>
        searchMatch(
          trimmedSearchValue,
          item[SuggestKeywordProperty.SUGGEST_KEYWORD],
        ),
      ),
    [suggestKeywords, trimmedSearchValue],
  );

  const filteredSuggestKeywordsKeys = useMemo(
    () =>
      new Set(
        filteredSuggestKeywords.map((item) => item[SUGGEST_KEYWORD_UNIQUE_KEY]),
      ),
    [filteredSuggestKeywords],
  );

  const chosenKeys = useMemo(
    () => new Set(chosen.map((item) => item[MANUAL_UNIQUE_KEY])),
    [chosen],
  );

  const readyKeys = useMemo(
    () => new Set(readyKeywords.map((item) => item[MANUAL_UNIQUE_KEY])),
    [readyKeywords],
  );

  const filteredChosenUnionReadyKeys = useMemo(
    () =>
      getIntersection(
        getUnion(chosenKeys, readyKeys),
        filteredSuggestKeywordsKeys,
      ),
    [chosenKeys, readyKeys, filteredSuggestKeywordsKeys],
  );

  const readyKeywordsIncludingZero = useMemo(
    () =>
      readyKeywords.some(
        (item) =>
          item[ManualKeywordProperty.PRICE] <= 0 ||
          item[ManualKeywordProperty.PRICE] > bidRange[1],
      ),
    [bidRange, readyKeywords],
  );

  const handleOutlier = () => {
    const existingKeywords = new Set(
      [...chosen, ...readyKeywords].map(
        (item) => item[ManualKeywordProperty.KEYWORD],
      ),
    );

    // Split by common delimiters (space, comma, newline), trim, filter empty and duplicates
    const newKeywordsToAdd = Array.from(
      new Set(
        trimmedSearchValue
          .replace(/(\\n|<br\s*\/?>)/gi, '') // Remove newlines and <br> tags
          .split(/[,，]/) // Split only by English (,) or Chinese (，) comma
          .map((kw) => kw.trim()) // Trim whitespace
          .filter((kw) => kw.length > 0) // Remove empty strings
          .filter((kw) => !existingKeywords.has(kw)), // Filter out already included keywords
      ),
    ).map((kw) => ({
      // Create new keyword objects
      [ManualKeywordProperty.ID]: undefined,
      [ManualKeywordProperty.KEYWORD]: kw,
      [ManualKeywordProperty.PRICE]: Number(
        suggestKeywords.find(
          (item) => item[SuggestKeywordProperty.SUGGEST_KEYWORD] === kw,
        )?.[SuggestKeywordProperty.CPC] ||
          bidRange[0] ||
          5,
      ), // Ensure price is always a number
    }));

    if (newKeywordsToAdd.length > 0) {
      const uniqueNewKeywords = newKeywordsToAdd.filter(
        (newKeyword) =>
          !readyKeywords.some(
            (readyKeyword) =>
              readyKeyword[ManualKeywordProperty.KEYWORD] ===
              newKeyword[ManualKeywordProperty.KEYWORD],
          ),
      );

      if (uniqueNewKeywords.length > 0) {
        setReadyKeywords([...readyKeywords, ...uniqueNewKeywords]);
      }
    }
    // Clear search input after adding
    setSearchValue('');
  };

  const handleReadyBidChange = (uniqueManualKey: string, newPrice: number) => {
    setReadyKeywords(
      readyKeywords.map((item) =>
        item[MANUAL_UNIQUE_KEY] === uniqueManualKey
          ? { ...item, [ManualKeywordProperty.PRICE]: newPrice }
          : item,
      ),
    );
  };

  const handleReadyDelete = (uniqueManualKey: string) => {
    setReadyKeywords(
      readyKeywords.filter(
        (item) => item[MANUAL_UNIQUE_KEY] !== uniqueManualKey,
      ),
    );
  };

  const handleSuggestionSelect = (uniqueSuggestKey: string) => {
    if (!chosenKeys.has(uniqueSuggestKey)) {
      if (readyKeys.has(uniqueSuggestKey)) {
        setReadyKeywords(
          readyKeywords.filter(
            (item) => item[MANUAL_UNIQUE_KEY] !== uniqueSuggestKey,
          ),
        );
      } else {
        const newReadyKeyword = getSuggestKeywordsByKeys(
          filteredSuggestKeywords,
          new Set([uniqueSuggestKey]),
        );
        setReadyKeywords([...readyKeywords, ...newReadyKeyword]);
      }
    }
  };

  const handleSuggestionAllSelect = () => {
    if (
      filteredSuggestKeywordsKeys.size === filteredChosenUnionReadyKeys.size
    ) {
      setReadyKeywords(
        readyKeywords.filter(
          (item) => !filteredSuggestKeywordsKeys.has(item[MANUAL_UNIQUE_KEY]),
        ),
      );
    } else {
      const newlySelectedKeys = getComplement(
        filteredSuggestKeywordsKeys,
        getUnion(chosenKeys, readyKeys), // Use union of chosen and ready for comparison
      );
      const newReadyKeywords = getSuggestKeywordsByKeys(
        suggestKeywords, // Get from original suggestions to ensure correct data
        newlySelectedKeys,
      );
      // Add only the newly selected ones to the existing ready keywords
      setReadyKeywords([...readyKeywords, ...newReadyKeywords]);
    }
  };

  const handleComplete = () => {
    onChosenChange([...chosen, ...readyKeywords]);
  };

  //未選擇關鍵字時，自動勾選前300個推薦關鍵字
  useEffect(() => {
    if (
      !hasInitializedChecked &&
      suggestKeywords.length > 0 &&
      chosen.length === 0
    ) {
      const firstChecked = suggestKeywords.slice(0, 300);
      //未選擇任何關鍵字時
      const onlyNewAsManualKeywords: ManualKeywordType[] = firstChecked.map(
        (item) => ({
          [ManualKeywordProperty.ID]: undefined,
          [ManualKeywordProperty.KEYWORD]:
            item[SuggestKeywordProperty.SUGGEST_KEYWORD],
          [ManualKeywordProperty.PRICE]:
            Number(item[SuggestKeywordProperty.CPC]) || bidRange[0] || 5,
        }),
      );

      setReadyKeywords(onlyNewAsManualKeywords);
      setHasInitializedChecked(true);
    }
  }, [hasInitializedChecked, suggestKeywords, chosen, bidRange]);

  return (
    <Grid className={styles.ManualKeywordEditBoard}>
      <Row gutter={18}>
        <Col xs={14}>
          <RsInput
            value={searchValue}
            onChange={setSearchValue}
            size={RsInputSize.SMALL}
            frontAddon={<SearchIcon />}
            placeholder='請輸入關鍵字'
          />
          <OutlierKeyword
            show={customKeywordShow}
            value={trimmedSearchValue}
            onClick={handleOutlier}
            disabled={trimmedSearchValueAlreadyIncluded}
          />
          <h3 className='text-[20px] font-normal'>推薦關鍵字</h3>
          <div className='h-[74vh]'>
            <SuggestKeywordTable
              data={filteredSuggestKeywords}
              checkedKeys={filteredChosenUnionReadyKeys} // Highlight keys present in chosen OR ready
              disabledKeys={chosenKeys} // Disable selection only for keys already in 'chosen'
              onSelect={handleSuggestionSelect}
              onAllSelect={handleSuggestionAllSelect}
            />
          </div>
        </Col>
        <Col xs={10}>
          <div className='bg-[#F7F6FF] px-[12px]'>
            <div className='flex items-baseline'>
              <h3 className='text-[20px] font-normal'>已加入關鍵字</h3>
              <span className='ml-1 inline-block text-[15px] font-normal'>
                ({readyKeywords.length})
              </span>
            </div>
            <div className='h-[72vh]'>
              <ReadyKeywordTable
                data={readyKeywords}
                onBidChange={handleReadyBidChange}
                onDelete={handleReadyDelete}
              />
            </div>
            <div className='flex justify-end gap-[12px] py-[12px]'>
              <RsButton appearance='ghost' onClick={onCancel}>
                取消
              </RsButton>
              <RsButton
                appearance='primary'
                onClick={handleComplete}
                disabled={
                  readyKeywordsIncludingZero || readyKeywords.length === 0
                } // Also disable if no keywords added
              >
                完成
              </RsButton>
            </div>
          </div>
        </Col>
      </Row>
    </Grid>
  );
};
